// API client for planning grid and feedback endpoints
import { apiClient } from "@/services/apiClient";
import { PortalClient } from "@/services/portalClient";
import { MediaItem, MediaItemData } from "@/core/MediaItem/MediaItem.model";
import { UserRole } from "@/services/urlService";
import { getPortalTokenKey } from "@/domains/Approval/constants/portalAuth";
import type {
  GridData,
  PostFeedback,
  FeedbackSummary,
  FeedbackType,
} from "../types/grid.types";

const BASE_URL = "/api/planning";

// Helper to get access token for MediaItem conversion
const getAccessToken = (portalToken?: string): string | null => {
  // If portal token provided, use it
  if (portalToken) {
    return portalToken;
  }

  // Otherwise use admin token from localStorage
  const storedUser = localStorage.getItem("user");
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      return user.accessToken || null;
    } catch {
      return null;
    }
  }
  return null;
};

// Helper to get the appropriate API client
const getClient = (clientId: string, usePortal: boolean = false) => {
  // If we're in portal mode, use PortalClient (it will get token from localStorage)
  if (usePortal) {
    return new PortalClient({ clientId, role: 'client' });
  }
  // Otherwise use admin apiClient
  return apiClient;
};

// Helper to convert backend media items to MediaItem instances
const convertGridMediaItems = (grid: GridData, clientId: string, usePortal: boolean = false): GridData => {
  const portalToken = usePortal ? localStorage.getItem(getPortalTokenKey(clientId)) ?? undefined : undefined;
  const accessToken = getAccessToken(portalToken);
  // Use CLIENT role if in portal mode, otherwise ADMIN
  const userRole = usePortal ? UserRole.CLIENT : UserRole.ADMIN;

  // Convert media_items in each post to MediaItem instances
  const convertedGridItems = grid.gridItems.map(gridItem => {
    if (gridItem.post && gridItem.post.mediaItems) {
      return {
        ...gridItem,
        post: {
          ...gridItem.post,
          mediaItems: MediaItem.fromBackendArray(
            gridItem.post.mediaItems as unknown as MediaItemData[],
            userRole,
            clientId,
            accessToken || undefined
          ),
        },
      };
    }
    return gridItem;
  });

  // IMPORTANT: Sort grid items by position to ensure correct display order
  // Backend should already sort, but we enforce it here to guarantee order
  const sortedGridItems = convertedGridItems.sort((a, b) => a.position - b.position);

  return {
    ...grid,
    gridItems: sortedGridItems,
  };
};

// ============================================================================
// GRID MANAGEMENT
// ============================================================================

export interface CreateGridRequest {
  clientId: string;
  name: string;
  description?: string;
}

export interface UpdateGridRequest {
  clientId: string;
  name?: string;
  description?: string;
}

export const planningApi = {
  // Create new grid
  createGrid: async (data: CreateGridRequest): Promise<GridData> => {
    const response = await apiClient.post<{ success: boolean; grid: GridData }>(
      `${BASE_URL}/grids`,
      data
    );
    return convertGridMediaItems(response.grid, data.clientId);
  },

  // List all grids for client
  listGrids: async (clientId: string, usePortal: boolean = false): Promise<GridData[]> => {
    const client = getClient(clientId, usePortal);
    const response = await client.get<{ success: boolean; grids: GridData[] }>(
      `${BASE_URL}/grids`,
      { params: { client_id: clientId } }
    );

    console.log(`🔍 listGrids (usePortal=${usePortal}): Received ${response.grids.length} grids`);
    response.grids.forEach(grid => {
      console.log(`  Grid ${grid.id} "${grid.name}": ${grid.gridItems.length} items`);
      console.log(`    Item positions:`, grid.gridItems.map(item => `${item.position}(${item.post?.id || 'empty'})`).join(', '));
    });

    const convertedGrids = response.grids.map(grid => convertGridMediaItems(grid, clientId, usePortal));

    console.log(`🔍 After conversion:`);
    convertedGrids.forEach(grid => {
      console.log(`  Grid ${grid.id}: positions =`, grid.gridItems.map(item => item.position).join(', '));
    });

    return convertedGrids;
  },

  // Get specific grid with posts
  getGrid: async (gridId: number, clientId: string): Promise<GridData> => {
    const response = await apiClient.get<{ success: boolean; grid: GridData }>(
      `${BASE_URL}/grids/${gridId}`,
      { params: { client_id: clientId } }
    );
    return convertGridMediaItems(response.grid, clientId);
  },

  // Update grid metadata
  updateGrid: async (
    gridId: number,
    data: UpdateGridRequest
  ): Promise<GridData> => {
    const response = await apiClient.put<{ success: boolean; grid: GridData }>(
      `${BASE_URL}/grids/${gridId}`,
      data
    );
    return convertGridMediaItems(response.grid, data.clientId);
  },

  // Delete grid
  deleteGrid: async (gridId: number, clientId: string): Promise<void> => {
    await apiClient.delete(`${BASE_URL}/grids/${gridId}`, undefined, {
      params: { client_id: clientId },
    });
  },

  // Activate grid (show to client)
  activateGrid: async (gridId: number, clientId: string): Promise<GridData> => {
    const response = await apiClient.post<{ success: boolean; grid: GridData }>(
      `${BASE_URL}/grids/${gridId}/activate`,
      { clientId }
    );
    return convertGridMediaItems(response.grid, clientId);
  },

  // Deactivate grid
  deactivateGrid: async (gridId: number, clientId: string): Promise<GridData> => {
    const response = await apiClient.post<{ success: boolean; grid: GridData }>(
      `${BASE_URL}/grids/${gridId}/deactivate`,
      { clientId }
    );
    return convertGridMediaItems(response.grid, clientId);
  },

  // Mark grid as completed
  completeGrid: async (gridId: number, clientId: string): Promise<GridData> => {
    const response = await apiClient.post<{ success: boolean; grid: GridData }>(
      `${BASE_URL}/grids/${gridId}/complete`,
      { clientId }
    );
    return convertGridMediaItems(response.grid, clientId);
  },

  // ============================================================================
  // POST MANAGEMENT
  // ============================================================================

  // Add post to grid
  addPostToGrid: async (
    gridId: number,
    postId: string,
    position: number,
    clientId: string
  ): Promise<void> => {
    await apiClient.post(`${BASE_URL}/grids/${gridId}/posts`, {
      clientId: clientId,
      postId: postId,
      position,
    });
  },

  // Remove post from grid
  removePostFromGrid: async (
    gridId: number,
    postId: string,
    clientId: string
  ): Promise<void> => {
    await apiClient.delete(`${BASE_URL}/grids/${gridId}/posts/${postId}`, undefined, {
      params: { client_id: clientId },
    });
  },

  // Reorder posts in grid
  reorderPosts: async (
    gridId: number,
    positions: Array<{ postId: string; position: number }>,
    clientId: string
  ): Promise<GridData> => {
    const response = await apiClient.put<{ success: boolean; grid: GridData }>(
      `${BASE_URL}/grids/${gridId}/reorder`,
      {
        clientId: clientId,
        positions,
      }
    );
    return convertGridMediaItems(response.grid, clientId);
  },

  // ============================================================================
  // FEEDBACK
  // ============================================================================

  // Submit or update feedback
  submitFeedback: async (
    gridId: number,
    postId: string,
    clientId: string,
    feedbackType: FeedbackType,
    comment?: string,
    userEmail?: string
  ): Promise<PostFeedback> => {
    const response = await apiClient.post<{
      success: boolean;
      feedback: PostFeedback;
    }>(`${BASE_URL}/grids/${gridId}/feedback`, {
      clientId: clientId,
      postId: postId,
      feedbackType: feedbackType,
      comment,
      userEmail: userEmail,
    });
    return response.feedback;
  },

  // Get all feedback for grid
  getGridFeedback: async (
    gridId: number,
    clientId: string
  ): Promise<PostFeedback[]> => {
    const response = await apiClient.get<{
      success: boolean;
      feedback: PostFeedback[];
    }>(`${BASE_URL}/grids/${gridId}/feedback`, {
      params: { client_id: clientId },
    });
    return response.feedback;
  },

  // Get feedback summary
  getFeedbackSummary: async (
    gridId: number,
    clientId: string
  ): Promise<FeedbackSummary> => {
    const response = await apiClient.get<{
      success: boolean;
      summary: FeedbackSummary;
    }>(`${BASE_URL}/grids/${gridId}/feedback/summary`, {
      params: { client_id: clientId },
    });
    return response.summary;
  },
};
